import { serviceDayInTimeZone } from '@croissants/contracts/projections/service-day';

import type { Locale } from './types';

/**
 * Locale formatting through the runtime's built-in `Intl.*` - zero packages
 * (tech-stack section 2). Every helper takes an explicit IANA timezone: the
 * platform stores UTC instants and renders them in the operational location's
 * zone, never in the reader's (tech-stack section 8).
 *
 * Money is an integer count of minor units everywhere it crosses this boundary
 * (PRD NFR-003). These helpers divide by the currency's own exponent exactly once,
 * at the last possible moment, so no intermediate value is ever a float.
 */

export const DEFAULT_TIME_ZONE = 'Europe/Paris';

/** Minor-unit exponent per currency. EUR at launch; the table exists so a second one is data. */
const CURRENCY_EXPONENT: Readonly<Record<string, number>> = {
  EUR: 2,
};

function exponentFor(currency: string): number {
  return CURRENCY_EXPONENT[currency] ?? 2;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * `locale` is a formatting TAG rather than a `Locale`: every date helper below
 * passes `dateTag(locale)` and the rest pass the app locale straight through.
 * Widening it here is what lets those go through the same memo as the rest - a
 * fresh `Intl.DateTimeFormat` costs about a millisecond, and a table of thirty
 * rows asks for thirty of them.
 */
function dateTimeFormat(
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const cacheKey = `${locale}|${timeZone}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    formatterCache.set(cacheKey, formatter);
  }
  return formatter;
}

function toDate(instant: Date | string | number): Date {
  return instant instanceof Date ? instant : new Date(instant);
}

/**
 * The FORMATTING tag a locale writes its DATES with, which is not the locale.
 *
 * `en` alone resolves to `en-US`, which writes "Thursday, September 3",
 * "Sep 3, 2026" and a 12-hour clock. Every English surface here reads day-first
 * on a 24-hour dial, because the thing being described happens in Paris and is
 * read there - so the tag is `en-GB` and the COPY around it stays American
 * English (CLAUDE.md: an `en-GB` formatter "must never be corrected to
 * `en-US`"). French needs no widening: `fr` already writes day-first, and
 * leaving it exactly as it was is what makes the French output of every helper
 * below byte-identical to what it replaced.
 *
 * It is read by every helper that renders a MONTH or a WEEKDAY - the long date,
 * the long date with its time, the medium date - rather than by one of them,
 * because "which tag does English write a date with" is one question and this
 * product had two answers to it for as long as both existed.
 * `formatShortDay` and `formatDayMonth` are the exception and say so at their
 * own headers: they compose `dd/mm` from parts, so they are day-first in any
 * tag and pass the locale straight through.
 */
export function dateTag(locale: Locale | string): string {
  return locale === 'en' ? 'en-GB' : locale;
}

/**
 * The short month of a service day: "Sept" / "sept." (F10).
 *
 * THE FRENCH POINT IS DECIDED HERE, ONCE: it is kept, as `Intl` writes it,
 * because that is what every other date the guest reads prints - the delivery
 * time through `formatDate` says "26 sept." - and the picker's day chip, the
 * several-mornings summary and that delivery time are one month to a reader.
 * The summary used to strip the point itself and print "sept" two lines above
 * a "sept.". Formatted in UTC from midday: a service day is a calendar label,
 * not an instant.
 */
export function formatShortMonth(serviceDay: string, locale: Locale): string {
  return dateTimeFormat(dateTag(locale), 'UTC', { month: 'short' }).format(
    new Date(`${serviceDay.slice(0, 10)}T12:00:00.000Z`),
  );
}

/**
 * "Monday 7 August" / "lundi 7 aout" - the day label used on the timeline
 * (GST-036), day-first in both languages (founder, 2026-09-06).
 *
 * The default options are the long date; an explicit `options` narrows it to
 * whatever parts a caller wants - a weekday alone for a day card, a month and a
 * year for a period heading. The TAG is not a caller's to choose either way,
 * which is the whole reason it is applied here: the English half printed
 * "Monday, August 7" until then, month-first, while the docstring on this very
 * function had claimed "Monday 7 August" since the day it was written.
 *
 * The surfaces that read it, counted rather than assumed - the order screens on
 * both staff portals print `formatShortDateTime` and are NOT among them. A
 * guest: the chosen morning, the stay recap, the schedule, the window-conflict
 * notice, the shops list, the order list, the checkout's window label and the
 * order timeline, plus the dated stamp `stampTime` puts on anything that did
 * not happen on the delivery morning. An operator: a bakery's and a client's
 * "created" date, a house's next settlement-cadence effective date, and the
 * three pairing instants (paired at, code issued, PIN set). A partner: the
 * pairing "code issued" line. And the confirmation a guest is sent by SMS and
 * email (NTF-001).
 */
export function formatDate(
  instant: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
  options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' },
): string {
  return dateTimeFormat(dateTag(locale), timeZone, options).format(toDate(instant));
}

/** "09:15" in both locales - 24-hour, which is what a delivery window is written in. */
export function formatTime(
  instant: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  return dateTimeFormat(locale, timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(toDate(instant));
}

/**
 * "3 Sept 2026" / "3 sept. 2026" - THE medium date, day-first in both languages
 * (founder, 2026-09-06).
 *
 * The shape for a date that stands on its own in a sentence or a hint - when a
 * mandate was accepted, when a device was paired, when a receipt's order was
 * delivered - where the year matters and a weekday does not. `formatShortDay`
 * above is the TABLE cell, dd/mm under a column heading; this is the one a
 * person reads once.
 *
 * Stated here because it was stated in three places and one of them disagreed:
 * `apps/partner-app`'s `instant()` asked plain `en` for it and printed
 * "Sep 3, 2026" on the pairing and import screens, while the same portal's legal
 * identity section and the guest receipt both widened to `en-GB` at their own
 * call sites and printed "3 Sept 2026". Two shapes of one date, in one product.
 *
 * No time, deliberately: a medium date carries none, so there is no `hourCycle`
 * to get wrong. A moment on one line is `formatShortDateTime`, which pins h23.
 */
export function formatMediumDate(
  instant: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  return dateTimeFormat(dateTag(locale), timeZone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(toDate(instant));
}

/**
 * "Sam. 30/08" / "Sat 30/08": THE day a table cell carries, in both locales
 * (founder, 2026-09-03: every table shows a date and a time the same way).
 *
 * Assembled from parts rather than asked of one format string, for two
 * reasons. Every English surface reads day-first like Paris does (the `en-GB`
 * rule in CLAUDE.md), and a locale-formatted `dd/mm` is the one thing `en`
 * will not give. And Intl writes the French weekday in lower case - "sam." -
 * where the cell reads "Sam.", so the first letter is raised here, once.
 */
export function formatShortDay(
  instant: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  const parts = dateTimeFormat(locale, timeZone, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).formatToParts(toDate(instant));
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  const weekday = part('weekday');
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${part('day')}/${part('month')}`;
}

/**
 * "30/08" - the day and the month alone, day-first in both languages.
 *
 * `formatShortDay` above without its weekday, for the one place a weekday will
 * not fit: an axis tick, where thirty of these stand side by side. Composed from
 * the same parts and for the same reason - `en` will not give a day-first
 * `dd/mm`, and this product writes what Paris reads whichever language it is
 * being read in. Stated here rather than in a screen, so a chart cannot invent a
 * second answer to a question the tables already answer.
 */
export function formatDayMonth(
  instant: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  const parts = dateTimeFormat(locale, timeZone, {
    day: '2-digit',
    month: '2-digit',
  }).formatToParts(toDate(instant));
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  return `${part('day')}/${part('month')}`;
}

/** "Sam. 30/08 07:42" - THE moment a table cell carries: the day, then the 24-hour time. */
export function formatShortDateTime(
  instant: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  return `${formatShortDay(instant, locale, timeZone)} ${formatTime(instant, locale, timeZone)}`;
}

/**
 * The same short day off a SERVICE DAY (`YYYY-MM-DD`), never shifted by a zone:
 * anchored at noon UTC, far enough from both edges that no offset moves it. A
 * string that is not a service day comes back as it was.
 */
export function formatShortServiceDay(serviceDay: string, locale: Locale): string {
  const parsed = new Date(`${serviceDay}T12:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? serviceDay : formatShortDay(parsed, locale, 'UTC');
}

/**
 * "09:15-09:45" with an en dash, the Deliveroo pattern the PRD adopts for the ETA
 * range (GST-036). One dash character, chosen here so no surface picks its own.
 */
export function formatWindow(
  start: Date | string | number,
  end: Date | string | number,
  locale: Locale,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  return `${formatTime(start, locale, timeZone)}–${formatTime(end, locale, timeZone)}`;
}

/**
 * The calendar day an instant falls on, in the location's zone: YYYY-MM-DD.
 *
 * The derivation is `serviceDayInTimeZone` (`@croissants/contracts/projections`),
 * the platform's one answer to which morning an instant belongs to. What is
 * left here is what a SCREEN needs on top of it and the projection deliberately
 * has not got: the `Date | string | number` a component holds, and the default
 * zone.
 *
 * The copy this replaced built a fresh `Intl.DateTimeFormat` on every call -
 * about a millisecond each, on a guest picker that asks once per candidate
 * morning. The shared one is memoized per zone.
 */
export function toServiceDay(
  instant: Date | string | number,
  timeZone: string = DEFAULT_TIME_ZONE,
): string {
  return serviceDayInTimeZone(toDate(instant), timeZone);
}

/**
 * Formats integer minor units as currency: `formatMoney(1250, 'EUR', 'fr')` and
 * `formatMoney(1250, 'EUR', 'en')` both give "12,50\u20ac" / "12.50\u20ac".
 *
 * THE SYMBOL GOES AFTER THE NUMBER, in every locale, by founder decision - the
 * platform prices in euros for a Paris service and reads the same way whichever
 * language the screen is in. `Intl` will not do that: it places the symbol per
 * locale, so English rendered "\u20ac12.50" while French beside it rendered
 * "12,50\u20ac", and the same figure changed shape between the two halves of one
 * product. The separators, the grouping and the minus sign are still the
 * locale's own - only the symbol is moved, and it is moved by rebuilding the
 * formatter's OWN parts rather than by string surgery on the result, so a
 * currency whose symbol contains a digit or a dot cannot be mangled.
 *
 * The whitespace `Intl` inserts between number and symbol is dropped with it:
 * "12,50\u20ac", not "12,50 \u20ac". Any other literal a locale emits survives.
 */
export function formatMoney(
  minorUnits: number,
  currency: string,
  locale: Locale,
  options: { hideDecimalsWhenWhole?: boolean } = {},
): string {
  if (!Number.isInteger(minorUnits)) {
    throw new TypeError(
      `formatMoney expects integer minor units, received ${minorUnits}. Money is never a float (NFR-003).`,
    );
  }
  const exponent = exponentFor(currency);
  const whole = minorUnits % 10 ** exponent === 0;
  const fractionDigits = options.hideDecimalsWhenWhole && whole ? 0 : exponent;
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).formatToParts(minorUnits / 10 ** exponent);
  const symbol = parts
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('');
  const number = parts
    .filter(
      (part) => part.type !== 'currency' && !(part.type === 'literal' && part.value.trim() === ''),
    )
    .map((part) => part.value)
    .join('');
  return `${number}${symbol}`;
}

/**
 * Signed money, the sign always shown: "+12,50\u20ac", "-4,50\u20ac", and zero
 * unsigned. For a drift, a fee divergence or a statement's adjustment line,
 * where the direction is the point (PRD CON-012, PAY-025). One owner: Internal
 * and Partner each carried a copy of this until the centralization audit (F8).
 */
export function formatSignedMoney(minorUnits: number, currency: string, locale: Locale): string {
  const formatted = formatMoney(Math.abs(minorUnits), currency, locale);
  if (minorUnits < 0) return `-${formatted}`;
  if (minorUnits > 0) return `+${formatted}`;
  return formatted;
}

/** "30%" from basis points, so a rate is never carried as a float either. */
export function formatBasisPoints(basisPoints: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(basisPoints / 10_000);
}

/** Joins a list with the locale's own conjunction: "A, B and C" / "A, B et C". */
export function formatList(items: readonly string[], locale: Locale): string {
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
}

/** "in 12 minutes" / "dans 12 minutes" - used for ETA copy, never for a promise. */
export function formatRelativeMinutes(minutes: number, locale: Locale): string {
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(minutes, 'minute');
}

/**
 * How many of the next unit make one of the one after it. Seconds are the
 * smallest thing this platform ever reports a distance in.
 */
const RELATIVE_UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 30],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
];

/**
 * "2 hours ago" / "il y a 2 heures" - a distance from a GIVEN instant, in the
 * largest unit that still says a number.
 *
 * `now` is a required argument and there is deliberately no `new Date()`
 * default, for the same reason `todayServiceDay` has none: outside production
 * the platform runs on the rehearsal clock, and a browser-dated default renders
 * a heartbeat from twenty seconds ago as a day old.
 *
 * The magnitude is TRUNCATED rather than rounded, so "1 hour ago" holds until
 * two hours have really passed - a device last seen at 07:50 does not read "2
 * hours ago" at 09:20.
 *
 * Two edges, both of which reach this from the fleet list.
 *
 *  - A FUTURE instant is clamped to zero, which `numeric: 'auto'` renders as
 *    "now" / "maintenant". A counter beats on its own clock and the platform
 *    stamps on the platform's, so a beat a few hundred milliseconds ahead is
 *    ordinary - and "in 1 second" beside a heartbeat that has already arrived
 *    reads as a bug in the platform rather than as clock drift. Only the
 *    DISPLAY is clamped; nothing else in the platform decides anything from a
 *    distance.
 *  - A non-finite input answers an em dash instead of throwing. Every unit's
 *    ceiling is passed by `NaN`, so the fall-through reached
 *    `Intl.RelativeTimeFormat.format(NaN)`, which is a `RangeError` - and one
 *    unreadable stored instant would take a whole table down with it, which is
 *    the opposite of what an empty cell costs.
 */
export function formatRelativeTime(
  instant: Date | string | number,
  now: Date | string | number,
  locale: Locale,
): string {
  const distanceMs = toDate(instant).getTime() - toDate(now).getTime();
  if (!Number.isFinite(distanceMs)) return '\u2014';
  let value = Math.min(0, distanceMs) / 1000;
  for (const [unit, perNext] of RELATIVE_UNITS) {
    if (Math.abs(value) < perNext) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        Math.trunc(value),
        unit,
      );
    }
    value /= perNext;
  }
  /* Unreachable: the last step's ceiling is infinite. */
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.trunc(value), 'year');
}
